import { Anchor, Group, Stack, Text } from '@mantine/core';
import { Link } from 'wouter';
import { cancelledWayForward } from '@/components/InvoiceDetailsModal/documentActions';
import { ReissueInstallment } from '@/components/InvoiceDetailsModal/ReissueInstallment';
import type { InvoiceDetails, TDocumentGroup } from '@/models/invoices';
import { useOffer } from '@/models/offers';
import { IssueNewProformaButton } from '@/pages/offers/OfferHubPage';

const Dot = () => (
  <Text span fz="xs" c="dimmed">
    ·
  </Text>
);

/**
 * The offer line above the action bar: after a cancellation, the way forward (the proforma that
 * replaced it, Issue new proforma, an installment issued again). The offer itself is in the
 * related documents. A renewal document has the renewal instead.
 */
export function OfferLine({
  group,
  proforma,
}: {
  group: TDocumentGroup | undefined;
  /** The proforma of the page, for the way forward after a cancel. */
  proforma?: InvoiceDetails;
}) {
  if (!group) return null;

  if (group.excluded === 'renewal') {
    return (
      <Group gap={6} wrap="wrap">
        <Text fz="xs" fw={600}>
          Renewal
        </Text>
        <Dot />
        <Anchor component={Link} to="~/renewals" fz="xs">
          Renewals
        </Anchor>
        <Dot />
        <Text fz="xs" c="dimmed">
          not part of the order's documents
        </Text>
      </Group>
    );
  }
  if (!group.offer || !proforma || proforma.status !== 'cancelled') return null;

  return (
    <Stack gap={4}>
      <ReissueInstallment reissue={proforma.reissue} />
      <CancelledWayForward proforma={proforma} group={group} />
    </Stack>
  );
}

/**
 * A cancelled proforma that is not a plan installment: the proforma that replaced it, or Issue
 * new proforma by the offer's owner (the same control and gate as on the offer page).
 */
function CancelledWayForward({
  proforma,
  group,
}: {
  proforma: InvoiceDetails;
  group: TDocumentGroup;
}) {
  const way = cancelledWayForward(proforma, group.proformas);
  const accepted = group.offer?.accepted !== false;
  const offer = useOffer(
    way?.kind === 'new-proforma' && accepted
      ? (proforma.offerId ?? undefined)
      : undefined,
  );
  if (!way) return null;
  if (way.kind === 'replaced') {
    return (
      <Text fz="xs">
        Replaced by proforma{' '}
        <Anchor component={Link} to={`~/invoices/${way.id}`} fz="xs">
          {way.number}
        </Anchor>
        .
      </Text>
    );
  }
  if (!accepted) {
    return (
      <Text fz="xs" c="dimmed">
        The offer was unlocked: a new proforma is issued when the client accepts
        it again.
      </Text>
    );
  }
  const o = offer.data;
  if (o?.proformaState?.newProforma && o.mayUnlock) {
    return (
      <Group gap={6} wrap="wrap">
        <Text fz="xs">The offer has no active proforma.</Text>
        <IssueNewProformaButton offer={o} />
      </Group>
    );
  }
  return (
    <Text fz="xs" c="dimmed">
      The offer has no active proforma: its owner issues a new one, unlocks or
      archives the offer.
    </Text>
  );
}
